<template>
  <article class="feature-page">
    <div class="max-w-4xl mx-auto px-4 py-10">
      <h2 class="text-2xl lg:text-3xl font-bold text-center mb-4">
        piceditor.io Auto Colorize: Bring Your Black and White Photos to Life with Vibrant Colors
      </h2>
      <p class="text-xl text-center mb-6">Effortlessly Add Color to Your Black and White Photos</p>

      <div class="landing-page-feature-descriptions">
        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img1" :second="img1a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>Revitalize Precious Memories</h4>
            <p>
              Transform your old family photos, featuring loved ones and even pets, into colorful and lifelike images.
              Relive the warmth of those moments as if they were taken yesterday.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img2" :second="img2a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="col-span-12 lg:col-span-5">
            <h4>Restore Colors to Objects</h4>
            <p>
              Give a fresh look to photos of antique items, classic cars, or everyday objects by adding realistic
              colors. Perfect for preserving or showcasing vintage charm.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img3" :second="img3a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>Reimagine Natural Landscapes</h4>
            <p>
              Experience the beauty of nature in full color by restoring black-and-white photos of scenic landscapes.
              Add depth and vibrancy to forests, mountains, and skies.
            </p>
          </div>
        </div>
      </div>

      <p class="mb-4">
        Breathe new life into your old black-and-white photos by transforming them into colorful, vibrant memories.
        Whether you're looking to restore family photos, enhance artistic projects, or simply explore the power of
        color, our Auto Colorize tool offers an easy and intuitive experience. Using advanced AI technology, your photos
        are seamlessly colorized, creating realistic and stunning results while preserving the essence of the original
        image.
      </p>

      <h3 class="text-2xl font-bold mt-12 mb-6">Why Use Auto Colorize?</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 feature-page__specific">
        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/color.png" alt="colorize photos" />
          <strong>Effortless Colorization</strong>
          <p>
            Add vibrant colors to any black-and-white photo with just a few clicks, perfect for restoring old memories
            or creating new art.
          </p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/ai.png" alt="AI-powered" />
          <strong>AI-Powered Precision</strong>
          <p>
            Our cutting-edge AI technology ensures accurate and lifelike colorization, making your photos look naturally
            colorful.
          </p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/creative.png" alt="enhance creativity" />
          <strong>Revitalize Old Photos</strong>
          <p>
            Bring old black-and-white photos back to life by adding color, preserving memories in a new and vibrant way.
          </p>
        </div>
      </div>

      <h3 class="text-2xl font-bold mt-12 mb-6">How It Works</h3>
      <ol class="list-decimal pl-5 mb-6">
        <li>
          <strong>Upload Your Photo:</strong> Drag and drop your black-and-white image or upload it from your device.
        </li>
        <li>
          <strong>Automatic Colorization:</strong> Our AI instantly processes your photo and adds realistic colors,
          bringing it to life.
        </li>
        <li><strong>Preview:</strong> Preview the colorized version.</li>
        <li>
          <strong>Download:</strong> Download your newly colorized photo and share it with family, friends, or use it in
          your projects.
        </li>
      </ol>

      <p class="">
        Our Auto Colorize tool is optimized for both mobile and desktop devices, allowing you to easily add color to
        your black-and-white photos anytime, anywhere. No special skills are required—just your imagination and our
        user-friendly tool.
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
  import AppComparisonSlider from '&/AppComparisonSlider.vue'

  import img1 from '@/images/landing_pages/colorize_black_and_white/1.jpg'
  import img1a from '@/images/landing_pages/colorize_black_and_white/1_after.jpg'
  import img2 from '@/images/landing_pages/colorize_black_and_white/2.jpg'
  import img2a from '@/images/landing_pages/colorize_black_and_white/2_after.jpg'
  import img3 from '@/images/landing_pages/colorize_black_and_white/3.jpg'
  import img3a from '@/images/landing_pages/colorize_black_and_white/3_after.jpg'
</script>
